import { z } from "zod";
import { UserProfile } from "./account.js";
import { PublicUser } from "./friendship.js";

export const GroupRole = z.enum(["owner", "admin", "member"]);
export type GroupRole = z.infer<typeof GroupRole>;

/** Enums, not booleans -- a future third option must not be a breaking contract change
 * (design.md's edge-visible need). */
export const WhoCanInvite = z.enum(["owner_admin", "any_member"]);
export type WhoCanInvite = z.infer<typeof WhoCanInvite>;

/** "discoverable" is additive (human override #3, brief.md): a superset of "members_and_invitees"
 * that also lists the group in GET /v1/groups/search and exposes its non-member preview to any
 * signed-in user. The other two values' behaviour is unchanged. */
export const WhoCanView = z.enum(["members_and_invitees", "members_only", "discoverable"]);
export type WhoCanView = z.infer<typeof WhoCanView>;

export const CreateGroupRequest = z.object({
  name: z.string().min(1).max(80),
  description: z.string().max(280).optional(),
  whoCanInvite: WhoCanInvite.default("owner_admin"),
  /** Default flipped to "discoverable" (human override #3): a group is findable unless its
   * owner/admin dials it down, consistent with people-search's own default-on flip. */
  whoCanView: WhoCanView.default("discoverable"),
});
export type CreateGroupRequest = z.infer<typeof CreateGroupRequest>;

/** PATCH /v1/groups/:id -- every field optional, owner/admin-gated at the route. */
export const UpdateGroupRequest = z.object({
  name: z.string().min(1).max(80).optional(),
  description: z.string().max(280).nullable().optional(),
  whoCanInvite: WhoCanInvite.optional(),
  whoCanView: WhoCanView.optional(),
});
export type UpdateGroupRequest = z.infer<typeof UpdateGroupRequest>;

export const GroupView = z.object({
  id: z.string().min(1),
  name: z.string(),
  description: z.string().nullable(),
  avatarUrl: z.url().nullable(),
  whoCanInvite: WhoCanInvite,
  whoCanView: WhoCanView,
  memberCount: z.number().int().nonnegative(),
  viewerRole: GroupRole.nullable(),
  createdAt: z.iso.datetime(),
});
export type GroupView = z.infer<typeof GroupView>;

export const GroupMemberView = z.object({
  userId: z.string().min(1),
  profile: UserProfile,
  role: GroupRole,
  joinedAt: z.iso.datetime(),
});
export type GroupMemberView = z.infer<typeof GroupMemberView>;

/** GET /v1/groups/:id/members -- cursor, not just a count, per groups.feature's
 * huge-membership scenario. */
export const GroupMemberList = z.object({
  items: z.array(GroupMemberView),
  nextCursor: z.string().nullable(),
});
export type GroupMemberList = z.infer<typeof GroupMemberList>;

/** Mirrors flight.ts's join-code construction (CSPRNG wordlist). One active link per group at a
 * time; regenerating revokes the previous code. */
export const GroupInviteLink = z.object({
  code: z.string().min(1),
  url: z.url(),
  createdAt: z.iso.datetime(),
});
export type GroupInviteLink = z.infer<typeof GroupInviteLink>;

/** POST /v1/groups/invite-links/:code/join -- response is the resulting membership state, not a
 * bare 201, so the join reads back as "already a member" immediately. Idempotent: re-joining an
 * already-joined link returns 200 with the same shape (design.md's seamless-join requirement). */
export const JoinGroupResponse = z.object({
  group: GroupView,
  member: GroupMemberView,
});
export type JoinGroupResponse = z.infer<typeof JoinGroupResponse>;

/** POST /v1/groups/:id/members/:userId/role -- owner-only; the only path to add a co-owner (and
 * so the only path that makes "transfer ownership first" (the zero-owner-leave copy) actionable. */
export const UpdateMemberRoleRequest = z.object({
  role: GroupRole,
});
export type UpdateMemberRoleRequest = z.infer<typeof UpdateMemberRoleRequest>;

/** GET /v1/groups -- the caller's own groups only, cursor-paginated like GroupMemberList. Never a
 * public/discoverable listing: whoCanView doesn't gate this route at all, membership itself is the
 * only filter, so there is no enumeration surface to misuse as a directory. */
export const GroupList = z.object({
  items: z.array(GroupView),
  nextCursor: z.string().nullable(),
});
export type GroupList = z.infer<typeof GroupList>;

/** POST /v1/groups/:id/invites -- invites accepted friends; each one joins only after accepting in
 * their inbox. Idempotent per friendId: someone already in, asked or invited is skipped. */
export const InviteFriendsToGroupRequest = z.object({
  friendIds: z.array(z.string().min(1)).min(1).max(50),
});
export type InviteFriendsToGroupRequest = z.infer<typeof InviteFriendsToGroupRequest>;

/**
 * GET /v1/groups/search -- human override #3 (brief.md): a browsable, substring-matched group
 * directory, scoped server-side to `whoCanView: "discoverable"` groups only. Same identity-
 * confirmation-only cap as people-search's result shape: never the roster, never group content.
 */
export const GroupSearchResult = z.object({
  id: z.string().min(1),
  name: z.string(),
  description: z.string().nullable(),
  avatarUrl: z.url().nullable(),
  memberCount: z.number().int().nonnegative(),
});
export type GroupSearchResult = z.infer<typeof GroupSearchResult>;

export const GroupSearchResponse = z.object({
  items: z.array(GroupSearchResult),
  nextCursor: z.string().nullable(),
});
export type GroupSearchResponse = z.infer<typeof GroupSearchResponse>;

/** GET /v1/groups/:id/preview -- a non-member's view of a discoverable group found via search,
 * identical shape to GroupSearchResult (the override expands who can be *found*, never what is
 * *shown* once found); a distinct route from GET /v1/groups/:id so that route's existing
 * member/invitee-preview contract is untouched. */
export const GroupPreview = GroupSearchResult;
export type GroupPreview = z.infer<typeof GroupPreview>;

/** POST /v1/groups/:id/join-requests -- a request-to-join a discoverable group creates a `pending`
 * group_members row, distinct from an actual member/admin/owner role (design.md's recommendation,
 * mirroring friendships.feature's request/accept shape). Idempotent on repeat taps. */
export const RequestToJoinGroupResponse = z.object({
  status: z.literal("pending"),
  requestedAt: z.iso.datetime(),
});
export type RequestToJoinGroupResponse = z.infer<typeof RequestToJoinGroupResponse>;

/** GET /v1/groups/:id/join-requests -- owner/admin only, a pending-requester row. No `role`: a
 * pending request is explicitly not a member/admin/owner (groups.feature). */
export const GroupJoinRequestView = z.object({
  userId: z.string().min(1),
  profile: UserProfile,
  requestedAt: z.iso.datetime(),
});
export type GroupJoinRequestView = z.infer<typeof GroupJoinRequestView>;

export const GroupJoinRequestList = z.object({
  items: z.array(GroupJoinRequestView),
  nextCursor: z.string().nullable(),
});
export type GroupJoinRequestList = z.infer<typeof GroupJoinRequestList>;

/** POST /v1/groups/:id/bans -- owner/admin only. A ban ends the membership and keeps the person
 * from rejoining through a link or a join request. */
export const BanGroupMemberRequest = z.object({ userId: z.string().min(1) });
export type BanGroupMemberRequest = z.infer<typeof BanGroupMemberRequest>;

export const GroupBanView = z.object({ user: PublicUser, bannedAt: z.iso.datetime() });
export type GroupBanView = z.infer<typeof GroupBanView>;

/** GET /v1/groups/:id/bans -- owner/admin only. */
export const GroupBanList = z.object({ items: z.array(GroupBanView) });
export type GroupBanList = z.infer<typeof GroupBanList>;
